import { dateToReportMonth } from '@/components/uploadsMonthOptions'
import type { WarehouseQboBillMatch, WarehouseReconcileIgnoreItem } from '@/services/warehouseTypes'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'

const LINKED_CLASS = 'bg-emerald-50 text-emerald-800 ring-emerald-200'
const IGNORED_CLASS = 'bg-gray-100 text-gray-700 ring-gray-200'
const AMBER_CLASS = 'bg-amber-50 text-amber-800 ring-amber-200'
const MUTED_CLASS = 'bg-gray-50 text-gray-600 ring-gray-200'

const IGNORED_TOOLTIP = 'User ignored this row for the reconcile month.'

const QBO_STATUS_TOOLTIPS: Record<string, string> = {
  matched:
    'QBO bill total equals uploaded invoice total (within tolerance), and Holding coding matches invoice type (Holding > 0 for WH/BK/mixed; Holding $0 expected for direct).',
  clean:
    'QBO bill total equals uploaded invoice total (within tolerance), and Holding coding matches invoice type (Holding > 0 for WH/BK/mixed; Holding $0 expected for direct).',
  amount_mismatch:
    'Vendor+Doc# matched a QBO bill, but bill total ≠ uploaded invoice total.',
  miscategorized:
    'Bill found, but Holding vs COGS coding does not match invoice type.',
  missing_bill:
    'Uploaded invoice has no QBO bill with same vendor + Doc #.',
  direct_no_holding: 'Direct-to-store invoice — no 3PL Holding bill expected.',
}

export type QboMatchStatusBadge = {
  label: string
  className: string
  tooltip: string
}

export function qboRowReconcileMonth(
  match: Pick<WarehouseQboBillMatch, 'txn_date'>,
  fallbackMonth: string | null | undefined
): string | null {
  return dateToReportMonth(match.txn_date) ?? fallbackMonth ?? null
}

export function getActiveReconcileIgnore(
  ignores: WarehouseReconcileIgnoreItem[] | undefined,
  selectedMonth: string | null | undefined
): WarehouseReconcileIgnoreItem | null {
  if (!ignores?.length) return null
  const permanent = ignores.find((item) => item.scope === 'permanent')
  if (permanent) return permanent
  if (!selectedMonth) return null
  return (
    ignores.find(
      (item) => item.scope === 'month' && item.report_month === selectedMonth
    ) ?? null
  )
}

export function isReconcileIgnored(
  ignores: WarehouseReconcileIgnoreItem[] | undefined,
  selectedMonth: string | null | undefined
): boolean {
  return getActiveReconcileIgnore(ignores, selectedMonth) != null
}

export function humanCoverageStatus(status: string): string {
  if (status === 'clean') return 'Matched'
  if (status === 'needs_adjustment') return 'Needs adjustment'
  if (status === 'missing_bright_sites') return 'Needs link'
  if (status === 'missing_po') return 'Needs link'
  if (status === 'miscategorized') return 'Miscategorized'
  if (status === 'amount_mismatch') return 'Amount mismatch'
  if (status === 'missing_bill') return 'Missing bill'
  if (status === 'direct_no_holding') return 'Direct (no holding)'
  if (status === 'missing_invoice') return 'Missing invoice PDF'
  if (status === 'matched') return 'Matched'
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function qboMatchStatusLabel(
  match: WarehouseQboBillMatch,
  reconcileMonth: string | null | undefined
): string {
  if (getActiveReconcileIgnore(match.reconcile_ignores, reconcileMonth)) return 'Ignored'
  return humanCoverageStatus(match.status)
}

export function qboMatchStatusTooltip(
  match: WarehouseQboBillMatch,
  reconcileMonth: string | null | undefined
): string {
  if (getActiveReconcileIgnore(match.reconcile_ignores, reconcileMonth)) {
    return IGNORED_TOOLTIP
  }
  return QBO_STATUS_TOOLTIPS[match.status] ?? humanCoverageStatus(match.status)
}

function qboStatusBadgeClass(status: string): string {
  if (status === 'matched' || status === 'clean') return LINKED_CLASS
  if (status === 'direct_no_holding') return MUTED_CLASS
  return AMBER_CLASS
}

export function qboMatchStatusBadge(
  match: WarehouseQboBillMatch,
  reconcileMonth: string | null | undefined,
  asOf: Date = new Date()
): QboMatchStatusBadge {
  const tooltip = qboMatchStatusTooltip(match, reconcileMonth)
  if (getActiveReconcileIgnore(match.reconcile_ignores, reconcileMonth)) {
    return { label: 'Ignored', className: IGNORED_CLASS, tooltip }
  }
  const rowMonth = qboRowReconcileMonth(match, reconcileMonth)
  if (rowMonth != null && !uploadsErrorVisibleForMonth(rowMonth, asOf)) {
    return {
      label: humanCoverageStatus(match.status),
      className: MUTED_CLASS,
      tooltip,
    }
  }
  return {
    label: humanCoverageStatus(match.status),
    className: qboStatusBadgeClass(match.status),
    tooltip,
  }
}

export function qboMatchRowClass(
  match: WarehouseQboBillMatch,
  reconcileMonth: string | null | undefined,
  asOf: Date = new Date()
): string | undefined {
  if (getActiveReconcileIgnore(match.reconcile_ignores, reconcileMonth)) {
    return 'border-l-2 border-l-gray-300 bg-gray-50/60'
  }
  const rowMonth = qboRowReconcileMonth(match, reconcileMonth)
  if (rowMonth != null && !uploadsErrorVisibleForMonth(rowMonth, asOf)) {
    return undefined
  }
  if (match.status !== 'matched' && !isReconcileIgnored(match.reconcile_ignores, reconcileMonth)) {
    return 'border-l-2 border-l-amber-400 bg-amber-50/40'
  }
  return undefined
}
